import React, { useEffect, useState, useCallback } from 'react';
import { authClient } from '../../../lib/auth-client';
import { toast } from 'react-toastify';

interface PasskeyItem {
    id: string;
    name?: string;
    createdAt: string;
}

const SecuritySettings = () => {
    const { data: session } = authClient.useSession();
    const twoFactorEnabled = !!(session?.user as any)?.twoFactorEnabled;

    // Passkey state
    const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
    const [passkeysLoading, setPasskeysLoading] = useState(true);
    const [addingPasskey, setAddingPasskey] = useState(false);
    const [deletingPasskeyId, setDeletingPasskeyId] = useState<string | null>(null);

    const fetchPasskeys = useCallback(async () => {
        try {
            const { data } = await authClient.$fetch('/passkey/list-user-passkeys', { method: 'GET' });
            setPasskeys(Array.isArray(data) ? data : []);
        } catch {
            setPasskeys([]);
        } finally {
            setPasskeysLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPasskeys();
    }, [fetchPasskeys]);

    const handleAddPasskey = async () => {
        setAddingPasskey(true);
        try {
            const { error } = await authClient.passkey.addPasskey();
            if (error) {
                if ((error as any).code === 'ERROR_CEREMONY_ABORTED') return;
                toast.error(String(error.message || 'Erro ao adicionar passkey'));
            } else {
                toast.success('Passkey adicionada com sucesso!');
                fetchPasskeys();
            }
        } catch {
            // User cancelled browser dialog - not an error
        } finally {
            setAddingPasskey(false);
        }
    };

    const handleDeletePasskey = async (id: string) => {
        if (!window.confirm('Tem certeza que deseja remover esta passkey?')) return;
        setDeletingPasskeyId(id);
        try {
            await authClient.$fetch('/passkey/delete-passkey', {
                method: 'POST',
                body: { id },
            });
            toast.success('Passkey removida');
            setPasskeys((prev) => prev.filter((p) => p.id !== id));
        } catch {
            toast.error('Erro ao remover passkey');
        } finally {
            setDeletingPasskeyId(null);
        }
    };

    const [showEnableModal, setShowEnableModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);

    // Enable 2FA state
    const [enablePassword, setEnablePassword] = useState('');
    const [totpURI, setTotpURI] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [verifyCode, setVerifyCode] = useState('');
    const [enableStep, setEnableStep] = useState<'password' | 'verify'>('password');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Disable 2FA state
    const [disablePassword, setDisablePassword] = useState('');
    const [disableLoading, setDisableLoading] = useState(false);
    const [disableError, setDisableError] = useState('');

    const handleStartEnable = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!enablePassword.trim()) {
            setError('Por favor, insira sua senha');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { data, error: apiError } = await authClient.twoFactor.enable({
                password: enablePassword,
            });
            if (apiError) {
                setError(apiError.message || 'Erro ao habilitar 2FA');
            } else if (data) {
                setTotpURI((data as any).totpURI || '');
                setBackupCodes((data as any).backupCodes || []);
                setEnableStep('verify');
            }
        } catch {
            setError('Erro ao habilitar 2FA');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyEnable = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!verifyCode.trim()) {
            setError('Por favor, insira o código');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { error: apiError } = await authClient.twoFactor.verifyTotp({
                code: verifyCode.trim(),
            });
            if (apiError) {
                setError(apiError.message || 'Código inválido');
            } else {
                toast.success('Autenticação em duas etapas habilitada!');
                resetEnableModal();
            }
        } catch {
            setError('Erro ao verificar código');
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!disablePassword.trim()) {
            setDisableError('Por favor, insira sua senha');
            return;
        }
        setDisableLoading(true);
        setDisableError('');
        try {
            const { error: apiError } = await authClient.twoFactor.disable({
                password: disablePassword,
            });
            if (apiError) {
                setDisableError(apiError.message || 'Erro ao desabilitar 2FA');
            } else {
                toast.success('Autenticação em duas etapas desabilitada');
                resetDisableModal();
            }
        } catch {
            setDisableError('Erro ao desabilitar 2FA');
        } finally {
            setDisableLoading(false);
        }
    };

    const resetEnableModal = () => {
        setShowEnableModal(false);
        setEnablePassword('');
        setTotpURI('');
        setBackupCodes([]);
        setVerifyCode('');
        setEnableStep('password');
        setError('');
    };

    const resetDisableModal = () => {
        setShowDisableModal(false);
        setDisablePassword('');
        setDisableError('');
    };

    // Extract the secret from the TOTP URI for manual entry
    const totpSecret = totpURI ? new URL(totpURI).searchParams.get('secret') || '' : '';

    return (
        <div className="card">
            <div className="card-header">
                <h5 className="card-title mb-0">Segurança</h5>
                <p className="text-muted mb-0 mt-1">
                    Gerencie suas configurações de segurança e autenticação.
                </p>
            </div>
            <div className="card-body">
                {/* Two-Factor Authentication Section */}
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h6 className="mb-1">Autenticação em duas etapas (2FA)</h6>
                        <p className="text-muted mb-0 small">
                            {twoFactorEnabled
                                ? 'A autenticação em duas etapas está habilitada. Sua conta está mais protegida.'
                                : 'Adicione uma camada extra de segurança à sua conta usando um aplicativo autenticador.'}
                        </p>
                    </div>
                    <div>
                        {twoFactorEnabled ? (
                            <span className="badge bg-success-subtle text-success me-2">Ativo</span>
                        ) : (
                            <span className="badge bg-warning-subtle text-warning me-2">Inativo</span>
                        )}
                    </div>
                </div>

                <div className="mt-3">
                    {twoFactorEnabled ? (
                        <button
                            type="button"
                            className="btn btn-soft-danger btn-sm"
                            onClick={() => setShowDisableModal(true)}
                        >
                            Desativar 2FA
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-soft-primary btn-sm"
                            onClick={() => setShowEnableModal(true)}
                        >
                            Ativar 2FA
                        </button>
                    )}
                </div>

                {/* Enable 2FA Modal */}
                {showEnableModal && (
                    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {enableStep === 'password'
                                            ? 'Ativar autenticação em duas etapas'
                                            : 'Configurar aplicativo autenticador'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={resetEnableModal}
                                    />
                                </div>
                                <div className="modal-body">
                                    {enableStep === 'password' ? (
                                        <form onSubmit={handleStartEnable}>
                                            <p className="text-muted small">
                                                Para ativar a autenticação em duas etapas, confirme sua senha atual.
                                            </p>
                                            <div className="mb-3">
                                                <label htmlFor="enablePassword" className="form-label">
                                                    Senha atual
                                                </label>
                                                <input
                                                    id="enablePassword"
                                                    type="password"
                                                    className={`form-control ${error ? 'is-invalid' : ''}`}
                                                    value={enablePassword}
                                                    onChange={(e) => {
                                                        setEnablePassword(e.target.value);
                                                        setError('');
                                                    }}
                                                    autoFocus
                                                />
                                                {error && <div className="invalid-feedback">{error}</div>}
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-light"
                                                    onClick={resetEnableModal}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading && (
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                    )}
                                                    Continuar
                                                </button>
                                            </div>
                                        </form>
                                    ) : (
                                        <form onSubmit={handleVerifyEnable}>
                                            <p className="text-muted small">
                                                Escaneie o código abaixo com seu aplicativo autenticador
                                                (Google Authenticator, Authy, etc.) e insira o código gerado.
                                            </p>

                                            {/* TOTP Secret for manual entry */}
                                            {totpSecret && (
                                                <div className="mb-3">
                                                    <label className="form-label small">Chave manual:</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm font-monospace"
                                                            value={totpSecret}
                                                            readOnly
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(totpSecret);
                                                                toast.success('Chave copiada!');
                                                            }}
                                                        >
                                                            Copiar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* TOTP URI for QR code apps */}
                                            {totpURI && (
                                                <div className="mb-3">
                                                    <label className="form-label small">URI TOTP (para apps que aceitam URI):</label>
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm font-monospace text-truncate"
                                                            value={totpURI}
                                                            readOnly
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(totpURI);
                                                                toast.success('URI copiada!');
                                                            }}
                                                        >
                                                            Copiar
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Backup codes */}
                                            {backupCodes.length > 0 && (
                                                <div className="mb-3">
                                                    <div className="alert alert-warning small">
                                                        <strong>Códigos de backup:</strong> Guarde estes códigos em
                                                        um local seguro. Eles podem ser usados para acessar sua conta
                                                        caso perca o acesso ao aplicativo autenticador.
                                                    </div>
                                                    <div className="bg-light rounded p-2">
                                                        <code className="d-block text-center small">
                                                            {backupCodes.join('  ')}
                                                        </code>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mb-3">
                                                <label htmlFor="verifyCode" className="form-label">
                                                    Código de verificação
                                                </label>
                                                <input
                                                    id="verifyCode"
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    maxLength={6}
                                                    placeholder="000000"
                                                    className={`form-control text-center fs-5 letter-spacing-2 ${error ? 'is-invalid' : ''}`}
                                                    value={verifyCode}
                                                    onChange={(e) => {
                                                        setVerifyCode(e.target.value.replace(/\D/g, ''));
                                                        setError('');
                                                    }}
                                                    autoFocus
                                                />
                                                {error && <div className="invalid-feedback">{error}</div>}
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-light"
                                                    onClick={resetEnableModal}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading && (
                                                        <span className="spinner-border spinner-border-sm me-2" />
                                                    )}
                                                    Verificar e ativar
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Passkeys Section */}
                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h6 className="mb-1">Passkeys</h6>
                        <p className="text-muted mb-0 small">
                            Use biometria ou chave de segurança para entrar sem digitar senha.
                        </p>
                    </div>
                </div>

                <div className="mt-3">
                    {passkeysLoading ? (
                        <div className="text-center py-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {passkeys.length > 0 ? (
                                <div className="list-group list-group-flush mb-3">
                                    {passkeys.map((pk) => (
                                        <div
                                            key={pk.id}
                                            className="list-group-item d-flex justify-content-between align-items-center px-0"
                                        >
                                            <div>
                                                <span className="fw-medium">
                                                    {pk.name || 'Passkey'}
                                                </span>
                                                <small className="text-muted d-block">
                                                    Adicionada em {new Date(pk.createdAt).toLocaleDateString('pt-BR')}
                                                </small>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-soft-danger btn-sm"
                                                disabled={deletingPasskeyId === pk.id}
                                                onClick={() => handleDeletePasskey(pk.id)}
                                            >
                                                {deletingPasskeyId === pk.id ? (
                                                    <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                    <i className="ri-delete-bin-line"></i>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted small mb-3">Nenhuma passkey cadastrada.</p>
                            )}
                            <button
                                type="button"
                                className="btn btn-soft-primary btn-sm"
                                disabled={addingPasskey}
                                onClick={handleAddPasskey}
                            >
                                {addingPasskey && (
                                    <span className="spinner-border spinner-border-sm me-2" />
                                )}
                                Adicionar passkey
                            </button>
                        </>
                    )}
                </div>

                {/* Disable 2FA Modal */}
                {showDisableModal && (
                    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Desativar autenticação em duas etapas</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={resetDisableModal}
                                    />
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleDisable}>
                                        <div className="alert alert-danger small">
                                            <strong>Atenção:</strong> Desativar a autenticação em duas etapas tornará
                                            sua conta menos segura.
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="disablePassword" className="form-label">
                                                Confirme sua senha
                                            </label>
                                            <input
                                                id="disablePassword"
                                                type="password"
                                                className={`form-control ${disableError ? 'is-invalid' : ''}`}
                                                value={disablePassword}
                                                onChange={(e) => {
                                                    setDisablePassword(e.target.value);
                                                    setDisableError('');
                                                }}
                                                autoFocus
                                            />
                                            {disableError && (
                                                <div className="invalid-feedback">{disableError}</div>
                                            )}
                                        </div>
                                        <div className="d-flex justify-content-end gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-light"
                                                onClick={resetDisableModal}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-danger"
                                                disabled={disableLoading}
                                            >
                                                {disableLoading && (
                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                )}
                                                Desativar
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SecuritySettings;
